import { Button, Menu, MenuItem } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HamburgerIcon from "../../../components/icons/HamburgerIcon";
import Logo from "../../../components/icons/Logo";
import { dashboardAction } from "../../../redux/features/dashboardSlice";
import "./Header.scss";
import { AuthHelpers } from "../../../utils/auth.helpers";
import AgentMenuDetails from "./AgentMenuDetails";
import AgencyMenuDetails from "./AgencyMenuDetails";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { IProfileAgency, IProfileAgent } from "../../../@types/api-data.types";
import {
  getCdnImageUrl,
  getImageUrl,
  getProfileImageUrl,
} from "../../../utils/helpers/common";
import { AgentApi } from "../../../utils/api/Agent";
import { AgencyApi } from "../../../utils/api/Agency";
import { RootState } from "../../../redux/store";
import { useNavigate } from "react-router-dom";
import { CommonEnums } from "../../../enums/common.enums";
import { clientRoutes } from "../../../utils/routes";
import MainLogo from "../../../components/icons/MainLogo";
import NotificationIcon from "../../../components/icons/NotificationIcon";
import MainMobileIcon from "../../../components/icons/MainMobileIcon";
import HeaderNotificationIcon from "../../../components/icons/HeaderNotificationIcon";
import UpArrowIcon from "../../../components/icons/UpArrowIcon";
import DownArrowIcon from "../../../components/icons/DownArrowIcon";

const menuTypes = {
  logout: "logout",
};

interface ThemeColor {
  r: number;
  g: number;
  b: number;
  pixel_counts: number;
}

interface stateType {
  isLoading: boolean;
  profileMenu: boolean;
  agentData: IProfileAgent | null;
  agencyData: IProfileAgency | null;
  type: string;
  base_color: string;
  text_color: string;
  agency_logo: string;
  agency_logo_ref: any;
  brand_logo_ref: any;
  profile_image: string;
  profile_image_ref: any;
  theme_colors: ThemeColor[];
  base_color_primary: string;
  text_color_primary: string;
  use_office_logo: boolean;
  brand_data: any;
}

const Header = () => {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  let profileData = useSelector(
    (state: RootState) => state?.dashboard?.profileData,
  );
  const [state, setState] = useState<stateType>({
    isLoading: false,
    profileMenu: false,
    agentData: null,
    agencyData: null,
    type: "",
    base_color: "",
    text_color: "",
    agency_logo: "",
    agency_logo_ref: null,
    brand_logo_ref: null,
    profile_image: "",
    profile_image_ref: null,
    theme_colors: [],
    base_color_primary: "",
    text_color_primary: "",
    use_office_logo: true,
    brand_data: null,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setState({
      ...state,
      profileMenu: true,
    });
  };

  const onClickHamburger = () => {
    dispatch(dashboardAction?.setsideBar());
  };

  const getData = async () => {
    let data: any =
      LocalStorageHelpers?.getAgentData() ||
      LocalStorageHelpers?.getAgencyData();

    const resolveColors = (agency: any, brand: any) => {
      const isIndependent = agency?.agency_type === "independent";
      const useOfficeLogo = agency?.use_office_logo ?? true;

      const shouldUseAgency = isIndependent || useOfficeLogo || !brand;

      if (shouldUseAgency) {
        return {
          theme_colors: agency?.theme_colors || [],
          base_color_primary: agency?.base_color_primary?.hex || "",
          text_color_primary: agency?.text_color_primary?.hex || "",
          useOfficeLogo: true,
        };
      }

      return {
        theme_colors: brand?.theme_colors_response || brand?.theme_colors || [],
        base_color_primary: brand?.base_color_primary?.hex || "",
        text_color_primary: brand?.text_color_primary?.hex || "",
        useOfficeLogo: false,
      };
    };

    if (data?.role === "agent") {
      const agency = data?.agency_ref;
      const brandData = agency?.brand_id;
      const colors = resolveColors(agency, brandData);

      setState((prev) => ({
        ...prev,
        agentData: data,
        type: "agent",
        base_color: agency?.agency_base_color,
        brand_logo_ref: brandData?.brand_logo,
        use_office_logo: colors.useOfficeLogo,
        brand_data: brandData,
        agency_logo: agency?.profile_image_ref?.original_file_path,
        agency_logo_ref: agency?.profile_image_ref,
        profile_image: data?.profile_image_ref?.compressed_file_path,
        profile_image_ref: data?.profile_image_ref,
        text_color: agency?.design_text_color?.hex,
        theme_colors: colors.theme_colors,
        base_color_primary: colors.base_color_primary,
        text_color_primary: colors.text_color_primary,
      }));

      let res = await AgentApi.GetAgentProfile();
      if (res?.data?.success) {
        let newAgentData = res?.data?.data;
        const newAgency = newAgentData?.agency_ref;
        const newBrandData = newAgency?.brand_id;
        const newColors = resolveColors(newAgency, newBrandData);

        setState((prev) => ({
          ...prev,
          agentData: newAgentData,
          type: "agent",
          base_color: newAgency?.agency_base_color,
          agency_logo: newAgency?.profile_image_ref?.original_file_path,
          agency_logo_ref: newAgency?.profile_image_ref,
          profile_image: newAgentData?.profile_image_ref?.compressed_file_path,
          profile_image_ref: newAgentData?.profile_image_ref,
          text_color: newAgency?.design_text_color?.hex,
          use_office_logo: newColors.useOfficeLogo,
          brand_data: newBrandData,
          brand_logo_ref: newBrandData?.brand_logo,
          theme_colors: newColors.theme_colors,
          base_color_primary: newColors.base_color_primary,
          text_color_primary: newColors.text_color_primary,
        }));
      }
    } else {
      const brandData = data?.brand_id;
      const colors = resolveColors(data, brandData);

      setState((prev) => ({
        ...prev,
        agencyData: data,
        type: "agency",
        base_color: data?.agency_base_color,
        agency_logo: data?.profile_image_ref?.original_file_path,
        agency_logo_ref: data?.profile_image_ref,
        brand_logo_ref: brandData?.brand_logo,
        use_office_logo: colors.useOfficeLogo,
        brand_data: brandData,
        profile_image:
          data?.principal_agent_id?.profile_image_ref?.compressed_file_path,
        profile_image_ref: data?.principal_agent_id?.profile_image_ref,
        text_color: data?.design_text_color?.hex,
        theme_colors: colors.theme_colors,
        base_color_primary: colors.base_color_primary,
        text_color_primary: colors.text_color_primary,
      }));

      let res = await AgencyApi.getAgencyDetails();

      if (res?.data?.success) {
        let newAgencyData = res?.data?.data;
        const newBrandData = newAgencyData?.brand_id;
        const newColors = resolveColors(newAgencyData, newBrandData);

        setState((prev) => ({
          ...prev,
          agencyData: newAgencyData,
          type: "agency",
          base_color: newAgencyData?.agency_base_color,
          agency_logo: newAgencyData?.profile_image_ref?.original_file_path,
          agency_logo_ref: newAgencyData?.profile_image_ref,
          brand_logo_ref: newBrandData?.brand_logo,
          use_office_logo: newColors.useOfficeLogo,
          brand_data: newBrandData,
          profile_image:
            newAgencyData?.principal_agent_id?.profile_image_ref
              ?.compressed_file_path,
          profile_image_ref:
            newAgencyData?.principal_agent_id?.profile_image_ref,
          text_color: newAgencyData?.design_text_color?.hex,
          theme_colors: newColors.theme_colors,
          base_color_primary: newColors.base_color_primary,
          text_color_primary: newColors.text_color_primary,
        }));
      }
    }
  };

  // const getData = async () => {
  //   let data: any =
  //     LocalStorageHelpers?.getAgentData() ||
  //     LocalStorageHelpers?.getAgencyData();

  //   if (data?.role === "agent") {
  //     setState({
  //       ...state,
  //       agentData: data,
  //       type: "agent",
  //       base_color: data?.agency_ref?.agency_base_color,
  //       agency_logo: data?.agency_ref?.profile_image_ref?.original_file_path,
  //       agency_logo_ref: data?.agency_ref?.profile_image_ref,
  //       profile_image: data?.profile_image_ref?.compressed_file_path,
  //       profile_image_ref: data?.profile_image_ref,
  //       text_color: data?.agency_ref?.design_text_color?.hex,
  //       theme_colors: data?.agency_ref?.theme_colors || [],
  //     });
  //     let res = await AgentApi.GetAgentProfile();
  //     if (res?.data?.success) {
  //       let newAgentData = res?.data?.data;
  //       setState({
  //         ...state,
  //         agentData: newAgentData,
  //         type: "agent",
  //         base_color: newAgentData?.agency_ref?.agency_base_color,
  //         agency_logo:
  //           newAgentData?.agency_ref?.profile_image_ref?.original_file_path,
  //           agency_logo_ref: data?.profile_image_ref,
  //         profile_image: newAgentData?.profile_image_ref?.compressed_file_path,
  //         profile_image_ref: data?.principal_agent_id?.profile_image_ref,
  //         text_color: newAgentData?.agency_ref?.design_text_color?.hex,
  //         theme_colors: newAgentData?.agency_ref?.theme_colors || [],
  //       });
  //     }
  //   } else {
  //     setState({
  //       ...state,
  //       agencyData: data,
  //       type: "agency",
  //       base_color: data?.agency_base_color,
  //       agency_logo: data?.profile_image_ref?.original_file_path,
  //       agency_logo_ref: newAgencyData?.profile_image_ref,
  //       profile_image:
  //         data?.principal_agent_id?.profile_image_ref?.compressed_file_path,
  //          profile_image_ref: newAgencyData?.principal_agent_id?.profile_image_ref,
  //       text_color: data?.design_text_color?.hex,
  //       theme_colors: data?.theme_colors || [],
  //     });

  //     let res = await AgencyApi.getAgencyDetails();

  //     if (res?.data?.success) {
  //       let newAgencyData = res?.data?.data;
  //       setState({
  //         ...state,
  //         agencyData: newAgencyData,
  //         type: "agency",
  //         base_color: newAgencyData?.agency_ref?.agency_base_color,
  //         agency_logo: newAgencyData?.profile_image_ref?.original_file_path,
  //         profile_image:
  //           newAgencyData?.principal_agent_id?.profile_image_ref
  //             ?.compressed_file_path,
  //         text_color: newAgencyData?.design_text_color?.hex,
  //         theme_colors: newAgencyData?.theme_colors || [],
  //       });
  //     }
  //   }
  // };

  const handleLogout = () => {
    AuthHelpers.logOut();
  };

  const handleClose = () => {
    setState({
      ...state,
      profileMenu: false,
    });
  };

  const handleCloseMenuItem = (link?: string) => {
    if (link) {
      navigate(link);
    }

    handleClose();
  };

  useEffect(() => {
    getData();

    const userProfileImages = document.querySelectorAll(".user-profile-image");

    userProfileImages.forEach((profileImage) => {
      profileImage.addEventListener("error", (event: any) => {
        event.src = "images/profile-placeholder-image.png";
      });
    });
  }, [profileData]);

  const menuItem = [
    // {
    //   id: "1",
    //   type: "",
    //   menuName: "Switch to Agent Profile",
    // },
    {
      // id: "1",
      id: "view-profile",
      type: "",
      menuName: "View Profile",
      link:
        state?.type === CommonEnums?.userRole?.agent
          ? clientRoutes?.agentProfileView
          : clientRoutes?.agencyProfileView,
    },
    ...(state?.type === CommonEnums?.userRole?.agent
      ? [
          {
            // id: "2",
            id: "agent-subscription",
            type: "",
            menuName: "Agent Subscription",
            link: "",
          },
        ]
      : []),
    {
      // id: "3",
      id: "invitations",
      type: "",
      menuName: "Invitations",
      link: "/manage-invites",
    },
    // {
    //   // id: "4",
    //   id: "wallet-transactions",
    //   type: "",
    //   menuName: "Wallet & Transactions",
    //   link: "",
    // },
    // {
    //   id: "4",
    //   type: "",
    //   menuName: "Account",
    //   link: "",
    // },

    {
      // id: "4",
      id: "account",
      type: "account",
      menuName: "Account",
      subMenu: [
        {
          id: "sub-1",
          menuName: "Payment history",
          link:
            state?.type === CommonEnums?.userRole?.agent
              ? "/payment-history"
              : "/payment-history",
        },

        ...(state?.type === CommonEnums?.userRole?.agent
          ? [
              {
                id: "sub-2",
                menuName: "Ledger",
                link: "/ledger",
              },
            ]
          : []),

        ...(state?.type === CommonEnums?.userRole?.agent
          ? [
              {
                id: "sub-2",
                menuName: "Manage Subscription",
                link: "/manage-subscriptions",
              },
            ]
          : []),
      ],
    },
    // ...(state?.type === CommonEnums?.userRole?.agency
    //   ? [
    //       {
    //         id: "5",
    //         type: "",
    //         menuName: "Settings",
    //         link: `${clientRoutes?.accountSettings}?type=${CommonEnums?.accountSettingsTabs?.configure_property_stages?.key}`,
    //       },
    //     ]
    //   : []),
    {
      // id: "5",
      id: "settings",
      type: "",
      menuName: "Settings",
      link: clientRoutes.configurePropertyStages,
    },
    {
      // id: "6",
      id: "support-help",
      type: "",
      menuName: "Support & Help!",
      link: "",
    },
    {
      // id: "7",
      id: "logout",
      type: menuTypes.logout,
      menuName: "Logout",
      link: "",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector(".top-header");
      if (window.scrollY > 50) {
        header?.classList.add("scrolled");
      } else {
        header?.classList.remove("scrolled");
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const rgbToCss = (color?: ThemeColor) => {
    if (!color) return "transparent";
    return `rgb(${color.r}, ${color.g}, ${color.b})`;
  };

  const getProfileImageDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.medium_image_url) {
      return getCdnImageUrl(uploadRecord.medium_image_url);
    }
    if (uploadRecord.small_image_url) {
      return getCdnImageUrl(uploadRecord.small_image_url);
    }
    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }
    if (uploadRecord.compressed_file_path) {
      return getImageUrl(uploadRecord.compressed_file_path);
    }
    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }
    return "";
  };

  const getAgencyLogoDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }
    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }
    if (uploadRecord.compressed_file_path) {
      return getImageUrl(uploadRecord.compressed_file_path);
    }
    return "";
  };

  const getBrandLogoDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }
    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }
    if (uploadRecord.compressed_file_path) {
      return getImageUrl(uploadRecord.compressed_file_path);
    }
    return "";
  };

  return (
    <div className="header-main-container">
      <div
        className="top-header"
        style={{
          backgroundColor:
            state.base_color_primary || rgbToCss(state.theme_colors[0]),
        }}
      >
        <div className={`header-left-contain`}>
          <div className="hamburger-icon" onClick={onClickHamburger}>
            <HamburgerIcon />
          </div>
          <div className="logo">
            <div className="web-icon">
              <MainLogo />
            </div>
            <div className="mobile-icon">
              <MainMobileIcon />
            </div>
          </div>
        </div>

        <div className="header-divider"></div>

        <div
          className="main-header-agency-image"
          style={{
            backgroundColor:
              state.base_color_primary || rgbToCss(state.theme_colors[0]),
          }}
        >
          {/* <img
            // src={getAgencyLogoDisplayUrl(state?.agency_logo_ref)}
            src={
              getAgencyLogoDisplayUrl(state?.agency_logo_ref) ||
              getBrandLogoDisplayUrl(state?.brand_logo_ref) ||
              "/images/agency_placeholder_image.png"
            }
            alt=""
          /> */}
          <img
            src={
              state.use_office_logo
                ? getAgencyLogoDisplayUrl(state?.agency_logo_ref) ||
                  getBrandLogoDisplayUrl(state?.brand_logo_ref) ||
                  "/images/agency_placeholder_image.png"
                : getBrandLogoDisplayUrl(state?.brand_logo_ref) ||
                  getAgencyLogoDisplayUrl(state?.agency_logo_ref) ||
                  "/images/agency_placeholder_image.png"
            }
            alt=""
          />
        </div>

        <div className="profile-image-and-notification-icon">
          <div className="web-notification-icon">
            <HeaderNotificationIcon stroke={state?.text_color} />
          </div>
          <div className="mobile-notification-icon">
            <NotificationIcon />
          </div>
          <Button
            id="basic-button"
            onClick={handleClick}
            className="profile-logo"
          >
            <img
              className="user-profile-image"
              src={
                getProfileImageDisplayUrl(state?.profile_image_ref) ||
                getProfileImageUrl(state?.profile_image || "")
              }
              alt="User"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/images/profile-placeholder-image.png";
              }}
            />
          </Button>
        </div>
      </div>

      <div
        className={`profile-menu-container ${
          state?.profileMenu ? "" : "display-none"
        }`}
      >
        <div className="profile-menu-bg" onClick={handleClose}></div>

        <div className="profile-menu-box">
          {state?.type === "agent" ? (
            <AgentMenuDetails
              handleClose={handleClose}
              agentData={state?.agentData}
            />
          ) : (
            <AgencyMenuDetails
              handleClose={handleClose}
              agencyData={state?.agencyData}
            />
          )}
          <div className="menu-navigation-contain-container">
            {menuItem?.map((item) => {
              if (item.type === menuTypes.logout) {
                return (
                  <MenuItem onClick={handleLogout} key={item?.id}>
                    {item?.menuName}
                  </MenuItem>
                );
              }

              if (item.type === "account") {
                return (
                  <div className="account-menu-main" key={item?.id}>
                    <MenuItem
                      className="account-menu-item"
                      onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                    >
                      <span>{item?.menuName}</span>

                      <div className="account-arrow-icon">
                        {accountMenuOpen ? <UpArrowIcon /> : <DownArrowIcon />}
                      </div>
                    </MenuItem>

                    {accountMenuOpen && (
                      <div className="account-submenu-container">
                        {item?.subMenu?.map((subItem: any) => {
                          return (
                            <div
                              key={subItem?.id}
                              className="account-submenu-item"
                              onClick={() => handleCloseMenuItem(subItem?.link)}
                            >
                              {subItem?.menuName}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <MenuItem
                  onClick={() => handleCloseMenuItem(item?.link)}
                  key={item?.id}
                >
                  {item?.menuName}
                </MenuItem>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
